import React, { useEffect, useState } from 'react';
import AppButton from '../../../base/button';
import { FiPlus } from 'react-icons/fi';
import { useBeneficiaryStore } from '../../../../store';
import {
  IGetBeneficiariesParam,
  IGetBeneficiary,
} from '../../../../models/beneficiary.model';
import { AppTable } from '../../../base/base-table';
import EmptyBeneficiary from '../../../../assets/svgs/empty-beneficiary.svg';
import BeneficiaryDetails from '../modals/beneficiary-details';
import DeleteBeneficiary from '../modals/delete-beneficiary';
import Pagination from '../../../base/pagination';
import FilterInput from '../../../base/filter-input';
import { CurrencyDiv } from '../../../base/currency';
import { getBeneficiaryProperty } from '../../../../utils/beneficiary.util';
import PaymentBadge from '../../../base/payment-badge';
import FilterDropdown from '../../../base/filter-dropdown';

const BeneficiaryTable: React.FC<{ onAddNewBeneficiary: () => void }> = ({
  onAddNewBeneficiary,
}) => {
  // Filters
  const [filters, setFilters] = useState<IGetBeneficiariesParam>({
    accountHolderName: '',
    currencyCode: '',
    page: 1,
    pageSize: 10,
  });

  const {
    getAllBeneficiary,
    deleteBeneficiary,
    beneficiariesPagination,
    beneficiaries,
    loading: loadingBeneficiaries,
  } = useBeneficiaryStore();

  const [isDeleteBeneficiaryModalOpen, setIsDeleteBeneficiaryModalOpen] =
    useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] =
    useState<IGetBeneficiary | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const fetchBeneficiaries = async () => {
    await getAllBeneficiary(filters);
  };

  useEffect(() => {
    fetchBeneficiaries();
  }, [filters]);

  const handleDeleteClick = (beneficiary: IGetBeneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setIsDeleteBeneficiaryModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedBeneficiary) {
      await deleteBeneficiary(selectedBeneficiary.id);
      setIsDeleteBeneficiaryModalOpen(false);
      setSelectedBeneficiary(null);
      fetchBeneficiaries();
    }
  };

  const toggleDeleteModal = () => {
    setIsDeleteBeneficiaryModalOpen(!isDeleteBeneficiaryModalOpen);
    if (isDeleteBeneficiaryModalOpen) {
      setSelectedBeneficiary(null);
    }
  };

  const handleViewClick = (beneficiary: IGetBeneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setIsViewModalOpen(true);
  };

  const toggleViewModal = () => {
    setIsViewModalOpen(!isViewModalOpen);
    if (isViewModalOpen) {
      setSelectedBeneficiary(null);
    }
  };

  const clearFilters = () => {
    setFilters({
      accountHolderName: '',
      currencyCode: '',
      page: 1,
      pageSize: 10,
    });
  };

  const tableHeadings = [
    'Name',
    'Currency',
    'Account Number',
    'Region',
    'Action',
  ];

  const tableData = beneficiaries.map((ben) => [
    { value: ben?.id, isRowID: true },
    {
      value: (
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-medium">
              {ben.accountHolderName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium">{ben.accountHolderName}</p>
            <p className="text-sm text-gray-500">{ben.beneficiaryEmail}</p>
          </div>
        </div>
      ),
    },
    {
      value: (
        <div className="px-2 py-1 bg-primary/10 rounded-md w-fit">
          <CurrencyDiv currency={ben?.currencyCode ?? ''} />
        </div>
      ),
    },
    {
      value: (
        <div>
          {ben?.paymentType === 1 && (
            <div className="flex flex-col gap-1">
              <p className="font-medium">{getBeneficiaryProperty(ben)}</p>
              <PaymentBadge paymentType={1} />
            </div>
          )}
          {ben?.paymentType === 2 && (
            <div className="flex flex-col gap-1">
              <p className="font-medium">{getBeneficiaryProperty(ben)}</p>
              <PaymentBadge paymentType={2} />
            </div>
          )}
          {ben?.paymentType === 3 && (
            <div className="flex flex-col gap-1">
              <p className="font-medium">{getBeneficiaryProperty(ben)}</p>
              <PaymentBadge paymentType={3} />
            </div>
          )}
          {ben?.paymentType === 4 && (
            <div className="flex flex-col gap-1">
              <p className="font-medium">{getBeneficiaryProperty(ben)}</p>
              <PaymentBadge paymentType={4} />
            </div>
          )}
        </div>
      ),
    },
    {
      value: (
        <div className="flex items-center gap-2">
          <div className="px-2 py-1 bg-gray-100 rounded-md">
            <p className="font-medium">{ben.beneficiaryAddress.countryCode}</p>
          </div>
          <p className="text-sm text-gray-500">
            {ben.beneficiaryAddress.state}
          </p>
        </div>
      ),
    },
    {
      value: (
        <div className="flex items-center space-x-3">
          <button
            className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
            onClick={() => handleViewClick(ben)}
          >
            View Details
          </button>
          <span className="text-gray-300">|</span>
          <button
            className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
            onClick={() => handleDeleteClick(ben)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ]);

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-[#F1F2F4] bg-white p-6 shadow-sm">
      <div className="flex justify-between items-center">
        <div className="grid gap-2">
          <p className="text-2xl leading-[31.2px] font-bold">Beneficiaries</p>
          <p className="text-sm leading-[22.4px] font-medium text-[#687588]">
            Manage your beneficiaries
          </p>
        </div>
        <div className="w-fit">
          <AppButton
            icon={<FiPlus />}
            label="Add Beneficiary"
            type="light"
            clicked={onAddNewBeneficiary}
          />
        </div>
      </div>

      {!!beneficiaries.length && (
        <div className="flex items-center justify-between">
          <div className="w-full flex items-center gap-6">
            <FilterInput
              label="Search"
              type="text"
              filter={(value) => value.length > 2}
              onSearch={(value) =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  accountHolderName: value,
                }))
              }
              placeholder="Search by name..."
            />
            <FilterDropdown
              label="Currency Code"
              value={filters.currencyCode}
              options={['USD', 'NGN', 'GBP', 'EUR']}
              onSelect={(selectedOption) =>
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  currencyCode: selectedOption,
                }))
              }
            />
          </div>

          {(filters.accountHolderName || filters.currencyCode) && (
            <AppButton
              label="Reset"
              type="danger-light"
              size="md"
              fullWidth={false}
              clicked={clearFilters}
            />
          )}
        </div>
      )}

      <AppTable
        loading={loadingBeneficiaries}
        columns={tableHeadings}
        data={tableData}
        emptyState={
          <div className="min-h-[30rem] flex flex-col items-center gap-7 justify-center">
            <img
              src={EmptyBeneficiary}
              alt="EmptyBeneficiary"
              className="h-[100px] w-[100px]"
            />
            <div className="text-center">
              <p className="text-[#1F2937] font-bold text-2xl">
                No beneficiaries found
              </p>
              <p className="text-[#687588] text-sm">
                You haven&apos;t added any beneficiaries yet. Click on the Add
                Beneficiary button to create one.
              </p>
            </div>
          </div>
        }
      />

      {!!beneficiaries.length && (
        <Pagination
          pageSize={beneficiariesPagination.currentPageSize}
          currentPage={beneficiariesPagination.currentPage}
          totalCount={beneficiariesPagination.totalRecords}
          hasNextPage={beneficiariesPagination.hasNextPage}
          hasPrevious={beneficiariesPagination.hasPrevious}
          onPageChange={(page: number) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              page,
            }));
          }}
          onPageSizeChange={(pageSize: number) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              pageSize,
            }));
          }}
        />
      )}

      <DeleteBeneficiary
        isOpen={isDeleteBeneficiaryModalOpen}
        toggleModal={toggleDeleteModal}
        selectedBeneficiary={selectedBeneficiary}
        onConfirmDelete={handleConfirmDelete}
        loading={loadingBeneficiaries}
      />

      <BeneficiaryDetails
        isOpen={isViewModalOpen}
        toggleModal={toggleViewModal}
        selectedBeneficiary={selectedBeneficiary}
      />
    </div>
  );
};

export default BeneficiaryTable;
